import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfileSelector = ({ onSelectProfile, showBackButton = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const token = localStorage.getItem('spirify_token');
  const tf = t?.family || {};

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);

      const res = await fetch(`${API}/api/family/profiles?user_id=${userData.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setProfiles(data.profiles || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProfile = (profile) => {
    // Store selected profile in localStorage
    localStorage.setItem('spirify_active_profile', JSON.stringify({
      id: profile.id,
      pseudo: profile.pseudo,
      avatar: profile.avatar,
      is_main: profile.is_main
    }));
    
    toast.success(`${tf.switchedTo || 'Profil actif'}: ${profile.pseudo}`);
    
    if (onSelectProfile) {
      onSelectProfile(profile);
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-white text-xl">{t?.settings?.loading || 'Chargement...'}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-white mb-2">Spirify</h1>
        <p className="text-slate-400">{tf.whoIsLearning || 'Qui apprend aujourd\'hui ?'}</p>
      </div>

      {/* Profiles Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
        {profiles.map((profile) => (
          <button
            key={profile.id}
            onClick={() => handleSelectProfile(profile)}
            className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-slate-700/50 transition-all group"
          >
            <div className="relative">
              <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center text-5xl shadow-lg group-hover:scale-105 transition-transform">
                {profile.avatar}
              </div>
              {profile.is_main && (
                <div className="absolute -top-2 -right-2 bg-cyan-500 text-white text-xs px-2 py-0.5 rounded-full">
                  👑
                </div>
              )}
            </div>
            <span className="text-white font-medium text-lg">{profile.pseudo}</span>
            <div className="flex items-center gap-3 text-sm text-slate-400">
              <span>🔥 {profile.streak}</span>
              <span>⭐ {profile.xp_week} XP</span>
            </div>
          </button>
        ))}
      </div>

      {/* Back button */}
      {showBackButton && (
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-8 text-slate-400 hover:text-white transition-colors"
        >
          ← {t?.lesson?.backToDashboard || 'Retour'}
        </button>
      )}
    </div>
  );
};

export default ProfileSelector;
